import type { ChangeEvent, MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Grid,
  OutlinedInput,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { Loading } from '@/components/atoms/Loading';
import { None, Same, PartnerSelect } from '@/components/atoms/PartnerSelect';
import { format } from 'date-fns';
import { useScheduleQuery } from '@/hooks/useScheduleQuery';
import type { Assignment, NewAssignment, Shift } from '@/openapi';
import type { PartnerSelectOption } from '@/providers/PartnerSelectProvider';
import {
  PartnerSelectProvider,
  usePartnerSelect,
} from '@/providers/PartnerSelectProvider';
import { dateToString } from '@/util/date';
import { emdash } from '@/util/unicode';
import { Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAssignmentMutation } from '@/hooks/useAssignmentMutation';
import { usePartnersQuery } from '@/hooks/usePartnersQuery';
import { ShiftSelectAdd } from '@/components/organisms/ShiftSelectAdd';
import { selectPointsFromShift } from '@/util/shiftAssignments';
import { useNewAssignmentMutation } from '@/hooks/useNewAssignmentMutation';
import { useShiftsQuery } from '@/hooks/useShiftsQuery';

const dashSame = (
  workedPartnerName: string | undefined,
  assignedPartnerName: string | undefined
) => {
  if (workedPartnerName === undefined) {
    return emdash;
  }
  if (workedPartnerName === assignedPartnerName) {
    return '+';
  }
  return workedPartnerName;
};

interface EditAssignmentRowProps {
  assignment: Assignment;
}

const EditAssignmentRow: React.FC<EditAssignmentRowProps> = ({
  assignment,
}) => {
  const theme = useTheme();
  const snackbar = useSnackbar();

  const assignmentMutation = useAssignmentMutation();

  const [inEditPoints, setEditPoints] = useState<boolean>(false);

  const onCheckCut = async (
    event: ChangeEvent<HTMLInputElement>,
    assignment: Assignment
  ) => {
    assignment.cut = event.target.checked;
    try {
      await assignmentMutation.mutateAsync(assignment);
    } catch {
      snackbar.enqueueSnackbar('Assignment failed to update', {
        variant: 'error',
      });
    }
  };

  const onChangePoints = async (value: string, assignment: Assignment) => {
    const points = parseFloat(value);
    if (Number.isNaN(points) || assignment.points === points) {
      return;
    }

    assignment.points = points;
    try {
      await assignmentMutation.mutateAsync(assignment);
    } catch {
      snackbar.enqueueSnackbar('Assignment failed to update', {
        variant: 'error',
      });
    }
  };

  const partnerSelect = usePartnerSelect();
  return (
    <Grid
      container
      spacing={2}
      key={assignment.shiftSymbol}
      sx={{
        alignItems: 'center',
      }}>
      <Grid size={3}>
        <Typography>{assignment.shiftSymbol}</Typography>
      </Grid>
      <Grid
        sx={{
          textAlign: 'center',
        }}
        size={3}>
        <Typography>{assignment.partnerNickname}</Typography>
      </Grid>
      <Grid
        onClick={(event: MouseEvent) => {
          partnerSelect.startSelect(
            event.currentTarget,
            assignment.date,
            assignment
          );
        }}
        sx={{
          textAlign: 'center',
          '&:hover': { cursor: 'pointer' },
        }}
        size={3}>
        <Typography>
          {dashSame(
            assignment.workedPartnerNickname,
            assignment.partnerNickname
          )}
        </Typography>
      </Grid>
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
        size={1}>
        <Checkbox
          checked={assignment.cut}
          onChange={(event) => onCheckCut(event, assignment)}
        />
      </Grid>
      <Grid
        onClick={() => setEditPoints(true)}
        sx={{
          textAlign: 'center',
        }}
        size={2}>
        {inEditPoints ? (
          <ClickAwayListener onClickAway={() => setEditPoints(false)}>
            <OutlinedInput
              size="small"
              defaultValue={assignment.points}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  // prevent it from killing the modal
                  event.stopPropagation();
                  setEditPoints(false);
                } else if (event.key === 'Tab' || event.key === 'Enter') {
                  onChangePoints(event.currentTarget.value, assignment);
                  setEditPoints(false);
                }
              }}
              onBlur={(event) => {
                onChangePoints(event.currentTarget.value, assignment);
                setEditPoints(false);
              }}
              sx={{ width: theme.spacing(6) }}
            />
          </ClickAwayListener>
        ) : (
          <span>{assignment.points}</span>
        )}
      </Grid>
    </Grid>
  );
};

export interface EditWorkedProps {
  date: Date;
  hideModal: () => void;
}

export const EditWorked: React.FC<EditWorkedProps> = ({ date, hideModal }) => {
  const theme = useTheme();

  const atDate = dateToString(date);
  const { isLoading, data: schedule } = useScheduleQuery(atDate, atDate, false);

  const snackbar = useSnackbar();

  const assignmentMutation = useAssignmentMutation();
  const newAssignmentMutation = useNewAssignmentMutation();

  const { data: partners } = usePartnersQuery();
  const { data: shifts } = useShiftsQuery();

  const partnerOptions = [Same].concat(
    partners?.data
      .filter(
        (partner) =>
          (partner.status === 'partner' || partner.status === 'employee') &&
          !partner.endDate
      )
      .map((partner) => ({
        label: partner.initials ?? '',
        id: partner.id,
        group: '',
      })) ?? []
  );

  const assignments = useMemo(() => {
    if (!schedule || !shifts || !schedule.assignments[atDate]) {
      return Array<Assignment>();
    }

    const assignments: Array<Assignment> = [];
    Object.values(schedule.assignments[atDate]).forEach((shift) =>
      assignments.push(...shift)
    );
    return assignments
      .map((assignment) => {
        const shift = shifts.find((shift) => shift.id === assignment.shiftId);
        return {
          ...assignment,
          rank: shift?.rank ?? 1000000,
        };
      })
      .sort((a, b) => a.rank - b.rank);
  }, [schedule, shifts]);

  const shiftsInUse = assignments.map((assignment) => assignment.shiftId);

  const getPartnersAvailable = (date: string) => {
    if (!schedule) {
      return partnerOptions;
    }

    // figure out who no longer works on this date
    const terminatedPartnerIds =
      partners?.data
        .filter((partner) => partner.endDate && partner.endDate < date)
        .map((partner) => partner.id) ?? [];

    // figure out partners in unchanged assignments
    const assignedPartnerIds: Array<number> = [];
    for (const slot in schedule.assignments[date]) {
      if (schedule.assignments[date][slot]) {
        const unchangedAssignments = schedule.assignments[date][slot].filter(
          (assignment) => assignment.workedPartnerId !== assignment.partnerId
        );
        const nontrivial = unchangedAssignments
          .map((assignment) => assignment.workedPartnerId)
          .filter((id) => Boolean(id)) as Array<number>;
        assignedPartnerIds.push(...nontrivial);
      }
    }

    // figure out who is on vacation
    const vacationPartnerIds: Array<number> = [];
    for (const slot in schedule.vacations[date]) {
      if (schedule.vacations[date][slot]) {
        const nontrivial = schedule.vacations[date][slot]
          .map((vacation) => vacation.partnerId)
          .filter((id) => Boolean(id)) as Array<number>;
        vacationPartnerIds.push(...nontrivial);
      }
    }

    const unassigned = partnerOptions
      .filter(
        (partner) =>
          !terminatedPartnerIds.includes(partner.id) &&
          !assignedPartnerIds.includes(partner.id) &&
          !vacationPartnerIds.includes(partner.id)
      )
      .sort((a, b) => a.label.localeCompare(b.label));
    const vacation = partnerOptions
      .filter((partner) => vacationPartnerIds.includes(partner.id))
      .map((partner) => ({ ...partner, group: 'Vacation' }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return unassigned.concat(vacation);
  };

  const finishSelect = async (
    value: PartnerSelectOption | null,
    date: string,
    assignment?: Assignment
  ) => {
    if (assignment && value) {
      if (value === Same) {
        assignment.workedPartnerId = assignment.partnerId;
        assignment.workedPartnerNickname = assignment.partnerNickname;
      } else if (value === None) {
        assignment.workedPartnerId = undefined;
        assignment.workedPartnerNickname = undefined;
      } else {
        assignment.workedPartnerId = value.id;
        assignment.workedPartnerNickname = value.label;
      }

      try {
        await assignmentMutation.mutateAsync(assignment);
      } catch {
        snackbar.enqueueSnackbar('Assignment failed to update', {
          variant: 'error',
        });
      }
    }
  };

  const onAddShift = async (shift: Shift) => {
    const assignment: NewAssignment = {
      date: atDate,
      shiftId: shift.id,
      points: selectPointsFromShift(date, shift),
      published: true,
    };

    try {
      await newAssignmentMutation.mutateAsync(assignment);
    } catch {
      snackbar.enqueueSnackbar('Failed to add shift', {
        variant: 'error',
      });
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{ minWidth: theme.spacing(60), maxWidth: theme.spacing(100) }}>
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'right',
          height: theme.spacing(6),
        }}>
        <Button onClick={hideModal}>
          <Close />
        </Button>
      </Grid>
      <Box
        sx={{
          px: 6,
          pt: 1,
          pb: 2,
        }}>
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
          }}>
          Edit Schedule
        </Typography>
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
          }}>
          {format(date, 'MMM d, y')}
        </Typography>
        <Box
          sx={{
            p: 2,
          }}
        />
        {schedule ? (
          <PartnerSelectProvider
            getPartnersAvailable={getPartnersAvailable}
            finishSelect={finishSelect}>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center',
                pl: 1,
                pb: 2,
                width: '100%',
              }}>
              <Grid size={3}>
                <Typography variant="h4">Shift</Typography>
              </Grid>
              <Grid
                sx={{
                  textAlign: 'center',
                }}
                size={3}>
                <Typography variant="h4">Assigned</Typography>
              </Grid>
              <Grid
                sx={{
                  textAlign: 'center',
                }}
                size={3}>
                <Typography variant="h4">Worked</Typography>
              </Grid>
              <Grid
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
                size={1}>
                <Typography variant="h4">Cut</Typography>
              </Grid>
              <Grid size={2}>
                <Typography variant="h4">Points</Typography>
              </Grid>
            </Grid>
            <Box
              sx={{
                border: 2,
                borderColor: theme.palette.divider,
                mr: -3,
                maxHeight: theme.spacing(60),
                overflowY: 'scroll',
              }}>
              <Box
                sx={{
                  p: 1,
                }}>
                {assignments.map((assignment, index) => (
                  <EditAssignmentRow key={index} assignment={assignment} />
                ))}
                <Box
                  sx={{
                    width: theme.spacing(25),
                  }}>
                  <ShiftSelectAdd
                    shiftsToExclude={shiftsInUse}
                    onSelectShift={(shift) => onAddShift(shift)}
                  />
                </Box>
              </Box>
            </Box>
            <PartnerSelect value={null} useWithinDialog={true} />
          </PartnerSelectProvider>
        ) : isLoading ? (
          <Loading />
        ) : (
          'An error occured while loading the schedule.'
        )}
        <Box
          sx={{
            p: 1,
          }}
        />
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Button
            variant="contained"
            onClick={hideModal}
            sx={{ width: theme.spacing(10) }}>
            Close
          </Button>
        </Grid>
      </Box>
    </Paper>
  );
};
